import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { mapHospitalityReviewDoc } from '../utils/mapHospitalityReviewDoc';

/**
 * Custom hook to fetch the latest 20 hospitality reviews with brewery and user information.
 * Returns reviews array with brewery name and user name populated.
 */
function useHospitalityReviewsList() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchReviews = async () => {
      try {
        if (!cancelled) {
          setLoading(true);
          setError(null);
        }

        const reviewsRef = collection(db, 'hospitalityReview');
        const reviewsQuery = query(
          reviewsRef,
          orderBy('date', 'desc'),
          limit(20),
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);

        if (cancelled) return;

        if (!reviewsSnapshot.empty) {
          const reviewsData = await Promise.all(
            reviewsSnapshot.docs.map(async (reviewDoc) => {
              const reviewData = reviewDoc.data();
              const breweryId = reviewData.breweryId;
              const userId = reviewData.uid;

              let breweryName = 'Unknown Brewery';
              if (breweryId) {
                try {
                  const breweryDocRef = doc(db, 'breweries', breweryId);
                  const breweryDoc = await getDoc(breweryDocRef);
                  if (breweryDoc.exists()) {
                    breweryName = breweryDoc.data().name || breweryName;
                  }
                } catch (fetchError) {
                  console.error(`Error fetching brewery ${breweryId}:`, fetchError);
                }
              }

              let userName = 'Unknown User';
              if (userId) {
                try {
                  const userDocRef = doc(db, 'users', userId);
                  const userDoc = await getDoc(userDocRef);
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    userName =
                      userData.userName || userData.displayName || userName;
                  }
                } catch (fetchError) {
                  console.error(`Error fetching user ${userId}:`, fetchError);
                }
              }

              return mapHospitalityReviewDoc({
                reviewId: reviewDoc.id,
                reviewData,
                breweryName,
                userName,
              });
            }),
          );

          if (!cancelled) {
            setReviews(reviewsData);
          }
        } else if (!cancelled) {
          setReviews([]);
        }
      } catch (fetchError) {
        console.error('Error fetching hospitality reviews:', fetchError);
        if (!cancelled) {
          setError(fetchError.message);
          setReviews([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchReviews();

    return () => {
      cancelled = true;
    };
  }, []);

  return { reviews, loading, error };
}

export default useHospitalityReviewsList;
